import React, {useEffect, useMemo, useRef, useState} from 'react';
import {MaterialReactTable, type MRT_ColumnDef, useMaterialReactTable,} from 'material-react-table';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AccountTree,
    Add,
    Assessment,
    CameraAlt,
    Category,
    ChangeCircle,
    CheckBox,
    CheckBoxOutlineBlank,
    Clear,
    Computer,
    Description,
    FileUpload,
    Folder,
    Person,
    Psychology,
    Refresh,
    Report,
    Search,
    TrendingUp,
} from '@mui/icons-material';
import {ResourceType} from '../../../@types/resource-type';
import {BasicPage} from '../../../@types/spring-data-page';
import {ExplorerDatasetType, ExtendedTreeViewItem} from './config/explorer-mrt-types';
import {getResourceConfig, tableConfigs} from './config/explorer-table-config';
import {getEnabledDatasets} from './config/explorer-dataset-config';
import {ActionType, getResourceActions} from './config/explorer-actions-config';
import {getColumnOrder, getColumnsForDataset} from './config/explorer-columns-config';
import authService from '../../../services/auth.service';
import NewOccurrenceService from '../../../services/new-occurrence.service';
import {DescriptorTerm} from '../../../@types/descriptor-term';
import FoundryService from '../../../services/foundry.service';
import JobManagementService from '../../../services/job-management.service';
import groupService from '../../../services/group.service';
import Group from '../../../@types/group';
import {getResourceNameWithAscendants} from '../../../utils/resource-utils';
import {toast} from 'react-toastify';
import ConnectingAirportsIcon from '@mui/icons-material/ConnectingAirports';

interface ExplorerMRTProps {
    data: ExtendedTreeViewItem[];
    loading: boolean;
    currentDataset: ExplorerDatasetType;
    searchText: string;
    filters: Record<string, any>;
    page: BasicPage;
    matchingIds?: Set<string>; // IDs of items that match search
    onDatasetChange: (dataset: ExplorerDatasetType) => void;
    onSearchChange: (value: string) => void;
    onFilterChange: (key: string, value: any) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onRowClick: (row: ExtendedTreeViewItem) => void;
    onRowDoubleClick: (row: ExtendedTreeViewItem) => void;
    onEdit: (row: ExtendedTreeViewItem) => void;
    onDelete: (row: ExtendedTreeViewItem) => void;
    onCopy: (row: ExtendedTreeViewItem) => void;
    onAction?: (action: ActionType, row: ExtendedTreeViewItem) => void; // Generic action handler
    onRefresh?: () => void; // Refresh current dataset
    onOpenIRPWorkbook?: () => void; // Open IRP Workbook demo view
    toolbar?: {
        showCreateButton?: boolean;
        onCreateClick?: () => void;
        createButtonText?: string;
        /**
         * When a dataset can create more than one kind of thing, the Create button opens a menu of
         * these instead of creating directly. The Safety Models dataset holds safety models and
         * components, and a component has no other visible way in.
         */
        createOptions?: Array<{ label: string; onClick: () => void }>;
        showImportButton?: boolean;
        onImportClick?: () => void;
    };
}

/**
 * Material React Table implementation for the Explorer
 * Adapted from the prototype to work with real data
 */
export const ExplorerMRT: React.FC<ExplorerMRTProps> = ({
    data,
    loading,
    currentDataset,
    searchText,
    filters,
    page,
    matchingIds = new Set(),
    onDatasetChange,
    onSearchChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onRowDoubleClick,
    onEdit,
    onDelete,
    onCopy,
    onAction,
    onRefresh,
    toolbar,
}) => {
    // Let MRT manage its own expanded state internally

    const [localData, setLocalData] = useState<ExtendedTreeViewItem[]>(data);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedRow, setSelectedRow] = useState<ExtendedTreeViewItem | null>(null);

    // Working Area filter state for the Component Catalogue: only published Components owned by
    // the selected working area display (component-solution-spec.md section 5). Single-select,
    // unlike the source filters below, since "the selected working area" is singular.
    const [selectedWorkingArea, setSelectedWorkingArea] = useState<Group | null>(null);
    const [availableWorkingAreas, setAvailableWorkingAreas] = useState<Group[]>([]);

    // Filter state for Occurrence Reports
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [selectedDescriptorTerms, setSelectedDescriptorTerms] = useState<DescriptorTerm[]>([]);
    const [availableSources, setAvailableSources] = useState<{sourceName: string; reportCount: number}[]>([]);
    const sourceOptionLabel = (sourceName: string): string => {
        const reportCount = availableSources.find((s): boolean => s.sourceName === sourceName)?.reportCount;
        return reportCount === undefined ? sourceName : `${sourceName} (${Math.round(reportCount).toLocaleString()})`;
    };
    const [availableDescriptorTerms, setAvailableDescriptorTerms] = useState<DescriptorTerm[]>([]);
    const [filtersLoading, setFiltersLoading] = useState<boolean>(false);

    // Local input state for Foundry Events search (committed only on button click / Enter)
    const [foundrySearchInput, setFoundrySearchInput] = useState<string>('');

    // Foundry Events import-source dropdown state
    const [importMenuAnchor, setImportMenuAnchor] = useState<null | HTMLElement>(null);
    const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
    // Source name of the import job currently in flight (null when idle); drives the button spinner
    const [importingSource, setImportingSource] = useState<string | null>(null);
    // Interval handle for polling the import job to completion
    const importPollRef = useRef<number | null>(null);

    // Stop polling the import job status
    const stopImportPolling = (): void => {
        if (importPollRef.current) {
            clearInterval(importPollRef.current);
            importPollRef.current = null;
        }
    };

    // Clean up any in-flight polling on unmount
    useEffect(() => stopImportPolling, []);

    // Poll the import batch job until it reaches a terminal state, then notify success/failure
    const pollImportJob = (executionId: number, sourceName: string): void => {
        stopImportPolling();
        importPollRef.current = window.setInterval(async () => {
            try {
                const job = await JobManagementService.getJobDetails(executionId);
                const state = String(job?.status?.state || '').toUpperCase();

                if (state === 'COMPLETED') {
                    stopImportPolling();
                    setImportingSource(null);

                    // Pull created/updated/skipped counts from the job result for the toast
                    let details = job?.result?.details;
                    if (!details) {
                        try {
                            const result = await JobManagementService.getJobResult(executionId);
                            details = result?.details;
                        } catch (resultErr) {
                            // eslint-disable-next-line no-console
                            console.error('Failed to fetch foundry import job result:', resultErr);
                        }
                    }

                    if (details) {
                        const created = details.created ?? 0;
                        const updated = details.updated ?? 0;
                        const skipped = details.skipped ?? 0;
                        toast.success(
                            `Import Foundry events completed for source "${sourceName}": `
                            + `${created} created, ${updated} updated, ${skipped} skipped.`
                        );
                    } else {
                        toast.success(`Import Foundry events completed for source "${sourceName}".`);
                    }
                } else if (['FAILED', 'CANCELLED', 'TIMED_OUT'].includes(state)) {
                    stopImportPolling();
                    setImportingSource(null);
                    const detail = job?.status?.errorMessage || job?.status?.message || 'Unknown error';
                    toast.error(`Import ${state.toLowerCase()} for "${sourceName}": ${detail}`);
                }
                // Otherwise (PENDING/RUNNING) keep polling
            } catch (err) {
                // Transient status-fetch errors shouldn't abort the job; keep polling
                // eslint-disable-next-line no-console
                console.error('Failed to poll foundry import job status:', err);
            }
        }, 5000);
    };

    // Open the source dropdown for Foundry Events import; otherwise defer to the parent handler
    const handleImportClick = (event: React.MouseEvent<HTMLElement>): void => {
        if (currentDataset === ResourceType.FOUNDRY_EVENTS) {
            setImportMenuAnchor(event.currentTarget);
        } else if (toolbar?.onImportClick) {
            toolbar.onImportClick();
        }
    };

    // Launch the import batch job for the selected Foundry source, then poll for its outcome
    const handleImportSource = async (sourceName: string): Promise<void> => {
        setImportMenuAnchor(null);
        setImportingSource(sourceName);
        try {
            const job = await FoundryService.importFoundryEvents(sourceName);
            toast.info(`Import Foundry events job started for source "${sourceName}".`);

            const executionId = job?.executionId;
            if (executionId !== undefined && executionId !== null) {
                pollImportJob(executionId, sourceName);
            } else {
                // No execution id returned; we can only confirm submission
                setImportingSource(null);
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to import foundry events.');
            setImportingSource(null);
        }
    };

    // Update local data when prop changes
    useEffect(() => {
        setLocalData(data);
    }, [data]);

    // Fetch filter options when dataset changes to Occurrence Reports
    useEffect(() => {
        if (currentDataset === ResourceType.NEW_OCCURRENCE_REPORTS) {
            const fetchFilterOptions = async (): Promise<void> => {
                setFiltersLoading(true);
                try {
                    // Fetch sources and descriptor terms in parallel
                    const [sources, descriptorTerms] = await Promise.all([
                        NewOccurrenceService.getReportSources(),
                        NewOccurrenceService.getAvailableDescriptorTerms()
                    ]);

                    setAvailableSources(sources);
                    setAvailableDescriptorTerms(descriptorTerms);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to fetch filter options:', error);
                    // Set empty arrays on error
                    setAvailableSources([]);
                    setAvailableDescriptorTerms([]);
                } finally {
                    setFiltersLoading(false);
                }
            };

            fetchFilterOptions();
        } if (currentDataset === ResourceType.FOUNDRY_EVENTS) {
            FoundryService.getEventSources()
                .then(sources => setAvailableSources(sources))
                .catch(() => setAvailableSources([]));
        } else if (currentDataset === ResourceType.COMPONENT_CATALOGUE) {
            groupService.getGroups()
                .then(groups => setAvailableWorkingAreas(groups))
                .catch(() => setAvailableWorkingAreas([]));
        } else {
            // Clear filter selections when switching away from Occurrence Reports
            setSelectedSources([]);
            setSelectedDescriptorTerms([]);
            setAvailableSources([]);
            setAvailableDescriptorTerms([]);
            setSelectedWorkingArea(null);
            setAvailableWorkingAreas([]);
        }
    }, [currentDataset]);

    // Sync local filter state with parent's filters prop (source of truth)
    // This ensures chips remain visible even after UI refreshes
    useEffect(() => {
        if (currentDataset === ResourceType.NEW_OCCURRENCE_REPORTS || currentDataset === ResourceType.FOUNDRY_EVENTS) {
            // Sync source names from parent filters
            if (filters?.sourceNames && Array.isArray(filters.sourceNames)) {
                setSelectedSources(filters.sourceNames);
            } else if (!filters?.sourceNames) {
                setSelectedSources([]);
            }

            // Sync descriptor term IDs from parent filters
            // Need to reconstruct full DescriptorTerm objects from IDs
            if (filters?.descriptorTermIds && Array.isArray(filters.descriptorTermIds)) {
                // Only sync if availableDescriptorTerms has loaded
                if (availableDescriptorTerms.length > 0) {
                    const selectedTerms = availableDescriptorTerms.filter((term): boolean =>
                        filters.descriptorTermIds.includes(term.id)
                    );
                    setSelectedDescriptorTerms(selectedTerms);
                }
                // If availableDescriptorTerms is still loading, don't clear selections yet
            } else if (!filters?.descriptorTermIds) {
                setSelectedDescriptorTerms([]);
            }
        }

        if (currentDataset === ResourceType.COMPONENT_CATALOGUE) {
            if (filters?.groupId && availableWorkingAreas.length > 0) {
                setSelectedWorkingArea(
                    availableWorkingAreas.find((grp): boolean => String(grp.id) === String(filters.groupId)) ?? null,
                );
            } else if (!filters?.groupId) {
                setSelectedWorkingArea(null);
            }
        }
    }, [currentDataset, filters, availableDescriptorTerms, availableWorkingAreas]);

    // Note: Search expansion is now handled by MRT's built-in filtering
    // which will show matching rows without needing to manipulate expanded state


    // Get current configuration (fall back to the safety-model preset for datasets without a
    // dedicated entry -- getDefaultColumns dereferences this)
    const currentConfig = tableConfigs[currentDataset] ?? tableConfigs.safetyModels;

    // Get resource icon
    const getResourceIcon = (type: ResourceType): JSX.Element => {
        const resourceConfig = getResourceConfig(type, currentConfig);
        const iconColor = resourceConfig?.iconColor || 'primary';

        const iconProps = {
            fontSize: 'small' as const,
            sx: { color: `${iconColor}.main` },
        };

        switch (type) {
            case ResourceType.GROUPS:
                return <Folder {...iconProps} />;
            case ResourceType.SAFETY_MODELS:
                return <AccountTree {...iconProps} />;
            case ResourceType.EVENT_SEQUENCES:
            case ResourceType.COMPONENT:
                return <Description {...iconProps} />;
            case ResourceType.WORKSHOPS:
                return <Assessment {...iconProps} />;
            case ResourceType.RISK_FORECASTS:
                return <TrendingUp {...iconProps} />;
            case ResourceType.OPERATIONAL_CHANGES:
                return <ChangeCircle {...iconProps} />;
            case ResourceType.OCCURRENCE_REPORTS:
                return <Report {...iconProps} />;
            case ResourceType.OCCURRENCE_REPORT_DESCRIPTORS:
                return <Category {...iconProps} />;
            case ResourceType.PERFORMANCE_SHAPING_FACTORS:
                return <Psychology {...iconProps} />;
            case ResourceType.USERS:
                return <Person {...iconProps} />;
            case ResourceType.SERVICE_USER_ACCOUNTS:
                return <Computer {...iconProps} />;
            case ResourceType.INTEGRATED_RISK_PICTURES:
                return <CameraAlt {...iconProps} />;
            case ResourceType.FOUNDRY_EVENTS:
                return <ConnectingAirportsIcon {...iconProps} />;
            default:
                return <Description {...iconProps} />;
        }
    };




    // Handle action menu
    const handleActionMenu = (event: React.MouseEvent<HTMLElement>, item: ExtendedTreeViewItem): void => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedRow(item);
    };

    // Handle menu actions
    const handleMenuAction = (action: ActionType): void => {
        if (!selectedRow) return;

        // Handle callbacks
        switch (action) {
            case 'edit':
                onEdit(selectedRow);
                break;
            case 'delete':
                onDelete(selectedRow);
                break;
            case 'duplicate':
                onCopy(selectedRow);
                break;
            default:
                // Handle all other actions through the generic handler
                if (onAction) {
                    onAction(action, selectedRow);
                }
                break;
        }

        setMenuAnchor(null);
        setSelectedRow(null);
    };

    // Define columns using the configuration
    const columns = useMemo<MRT_ColumnDef<ExtendedTreeViewItem>[]>(
        () => getColumnsForDataset(currentDataset, {
            currentConfig,
            matchingIds,
            handleActionMenu,
            getResourceIcon,
        }),
        [currentDataset, currentConfig, matchingIds]
    );

    const table = useMaterialReactTable({
        columns,
        data: localData,
        enableExpanding: currentDataset !== ResourceType.NEW_OCCURRENCE_REPORTS && currentDataset !== ResourceType.FOUNDRY_EVENTS,
        enableExpandAll: false,
        getSubRows: (row): ExtendedTreeViewItem[] | undefined => row.children,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        layoutMode: 'grid',
        // Keep the toolbar (with the dataset selector) and column headers fixed while only
        // the table body scrolls, so the dataset selector never scrolls out of view.
        enableStickyHeader: true,
        muiTablePaperProps: {
            sx: {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            },
        },
        muiTableContainerProps: {
            sx: {
                flex: 1,
                minHeight: 0,
            },
        },
        enableSorting: true,
        enableRowSelection: false,
        enableColumnOrdering: false, // Disable drag-and-drop column reordering
        enablePagination: currentDataset !== ResourceType.SAFETY_MODELS && currentDataset !== ResourceType.INTEGRATED_RISK_PICTURES,
        manualPagination: currentDataset !== ResourceType.SAFETY_MODELS && currentDataset !== ResourceType.INTEGRATED_RISK_PICTURES,
        // Foundry API does not report total pages; use a high sentinel so MRT keeps the Next button enabled.
        // For all other datasets, derive from the server response as usual.
        pageCount: currentDataset === ResourceType.FOUNDRY_EVENTS
            ? ((page as any).totalPages || Number.MAX_SAFE_INTEGER)
            : ((page as any).totalPages || 1),
        rowCount: (page as any).totalElements,
        muiPaginationProps: currentDataset === ResourceType.FOUNDRY_EVENTS
            ? {
                rowsPerPageOptions: [25, 50, 100],
                showFirstButton: true,
                showLastButton: false,
            }
            : {
                // Page size options: 10, 25, 50, 100 (capped at 100 to keep requests and rendering bounded)
                rowsPerPageOptions: [10, 25, 50, 100],
                showFirstButton: true,
                showLastButton: true,
            },
        muiBottomToolbarProps: currentDataset === ResourceType.FOUNDRY_EVENTS
            ? {
                sx: {
                    '& button[aria-label="Go to last page"]': {
                        display: 'none',
                    },
                },
            }
            : {},
        onPaginationChange: (updater): void => {
            if (typeof updater === 'function') {
                const currentPageIndex = page.offset;

                const newPagination = updater({
                    pageIndex: currentPageIndex,
                    pageSize: page.size,
                });
                if (newPagination.pageIndex !== currentPageIndex) {
                    onPageChange(newPagination.pageIndex);
                }
                if (newPagination.pageSize !== page.size) {
                    onPageSizeChange(newPagination.pageSize);
                }
            }
        },
        state: {
            pagination: {
                pageIndex: page.offset, 
                pageSize: page.size,
            },
            isLoading: loading,
            columnOrder: getColumnOrder(currentDataset), // Also set in state
        },
        displayColumnDefOptions: {
            'mrt-row-expand': {
                enableHiding: true,
                enableColumnActions: false,
                size: 0,
                maxSize: 0,
                muiTableHeadCellProps: { sx: { display: 'none' } },
                muiTableBodyCellProps: { sx: { display: 'none' } },
            },
        },
        initialState: {
            density: 'compact',
            columnOrder: getColumnOrder(currentDataset),
        },
        muiTableProps: {
            sx: {
                tableLayout: 'fixed',
            },
        },
        muiTableBodyRowProps: ({ row }): any => {
            const isArchived = currentDataset === ResourceType.INTEGRATED_RISK_PICTURES &&
                row.original.object?.archived === true;

            return {
                onClick: (): void => onRowClick(row.original),
                onDoubleClick: (): void => onRowDoubleClick(row.original),
                sx: {
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    userSelect: 'none',  // avoid highlighting text when double-clicking
                    ...(isArchived && {
                        backgroundColor: 'action.disabledBackground',
                        opacity: 0.7,
                    }),
                },
            };
        },
        renderEmptyRowsFallback: (): JSX.Element => {
            // Define empty state messages for each dataset type
            const emptyMessages: Partial<Record<ExplorerDatasetType, { title: string; description: string }>> = {
                [ResourceType.SAFETY_MODELS]: {
                    title: 'No Safety Models found',
                    description: 'Safety Models are the foundation for risk analysis. Create a new Safety Model to get started.'
                },
                [ResourceType.WORKSHOPS]: {
                    title: 'No Workshops found',
                    description: 'Workshops are collaborative sessions for safety assessments and scoring.'
                },
                [ResourceType.RISK_FORECASTS]: {
                    title: 'No Risk Forecasts found',
                    description: 'Risk Forecasts predict future safety trends based on historical data and models.'
                },
                [ResourceType.OPERATIONAL_CHANGES]: {
                    title: 'No Operational Changes found',
                    description: 'Operational Changes document modifications to systems and their safety impacts.'
                },
                [ResourceType.NEW_OCCURRENCE_REPORTS]: {
                    title: 'No Occurrence Reports found',
                    description: 'Occurrence Reports document safety events and incidents.'
                },
                [ResourceType.OCCURRENCE_REPORT_DESCRIPTORS]: {
                    title: 'No Descriptors found',
                    description: 'Descriptors categorize and tag occurrence reports for analysis.'
                },
                [ResourceType.GROUPS]: {
                    title: 'No Working Areas found',
                    description: 'Working Areas organize users and resources with specific permissions.'
                },
                [ResourceType.PERFORMANCE_SHAPING_FACTORS]: {
                    title: 'No PSF Templates found',
                    description: 'Performance Shaping Factor templates assess human performance influences.'
                },
                [ResourceType.USERS]: {
                    title: 'No Users found',
                    description: 'Users are individuals with access to the system.'
                },
                [ResourceType.SERVICE_USER_ACCOUNTS]: {
                    title: 'No Service Accounts found',
                    description: 'Service accounts provide API access for external integrations.'
                },
                [ResourceType.INTEGRATED_RISK_PICTURES]: {
                    title: 'No Integrated Risk Pictures found',
                    description: 'Integrated Risk Pictures analyze occurrence reports against event sequences to identify safety patterns.'
                },
            };

            const message = emptyMessages[currentDataset] || {
                title: 'No data found',
                description: 'No items match the current filters.'
            };

            return (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 8,
                    textAlign: 'center',
                    minHeight: '400px'
                }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {message.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {message.description}
                    </Typography>
                    {/* Show disabled Create button for IRP */}
                    {currentDataset === ResourceType.INTEGRATED_RISK_PICTURES && (
                        <Button
                            variant="outlined"
                            disabled
                            size="small"
                        >
                            Create IRP (Coming Soon)
                        </Button>
                    )}
                </Box>
            );
        },
        renderTopToolbar: (): JSX.Element => (
            <Box sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
                {/* First Row: Dataset Selector and New Button */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    {/* Dataset Selector and Refresh */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl size="small" sx={{
                            minWidth: 180,
                        }}>
                            <InputLabel>Dataset</InputLabel>
                            <Select
                                value={currentDataset}
                                label="Dataset"
                                onChange={(e: SelectChangeEvent<ExplorerDatasetType>): void =>
                                    onDatasetChange(e.target.value as ExplorerDatasetType)
                                }
                            >
                                {getEnabledDatasets().map((dataset): JSX.Element => (
                                    <MenuItem key={dataset.value} value={dataset.value}>
                                        {dataset.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Refresh Button */}
                        <IconButton
                            size="small"
                            onClick={onRefresh}
                            disabled={loading || !onRefresh}
                            title="Refresh current view"
                        >
                            <Refresh />
                        </IconButton>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Show Archived toggle for IRPs (super users only) */}
                        {currentDataset === ResourceType.INTEGRATED_RISK_PICTURES && authService.isSuperUser() && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filters?.archived === 'ARCHIVED'}
                                        onChange={(e): void => {
                                            onFilterChange('archived', e.target.checked ? 'ARCHIVED' : 'ACTIVE');
                                        }}
                                        size="small"
                                    />
                                }
                                label="Show Archived"
                                sx={{ mr: 1 }}
                            />
                        )}

                        {/* Show Superseded toggle for occurrence reports */}
                        {currentDataset === ResourceType.NEW_OCCURRENCE_REPORTS && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filters?.showSuperseded === true}
                                        onChange={(e): void => {
                                            onFilterChange('showSuperseded', e.target.checked);
                                        }}
                                        size="small"
                                    />
                                }
                                label="Show Superseded"
                                sx={{ mr: 1 }}
                            />
                        )}

                        {toolbar?.showCreateButton && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={(event): void => {
                                    if (toolbar.createOptions && toolbar.createOptions.length > 0) {
                                        setCreateMenuAnchor(event.currentTarget);
                                    } else {
                                        toolbar.onCreateClick?.();
                                    }
                                }}
                                size="small"
                                sx={{
                                    whiteSpace: 'nowrap',  // Prevent text wrapping
                                    minWidth: 'auto',      // Override MUI's default minWidth
                                    px: 2,                 // Add horizontal padding
                                    flexShrink: 0,         // Prevent button from shrinking
                                    '& .MuiButton-startIcon': {
                                        marginRight: 0.5
                                    }
                                }}
                            >
                                Create
                            </Button>
                        )}

                        {toolbar?.showImportButton && (
                            <Button
                                variant="outlined"
                                startIcon={importingSource
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <FileUpload />}
                                onClick={handleImportClick}
                                disabled={importingSource !== null}
                                size="small"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 'auto',
                                    px: 2,
                                    flexShrink: 0,
                                    '& .MuiButton-startIcon': {
                                        marginRight: 0.5
                                    }
                                }}
                            >
                                Import
                            </Button>
                        )}

                    </Box>
                </Box>

                {/* Filter Row: Source and Descriptor Term Filters (Occurrence Reports only) */}
                {currentDataset === ResourceType.NEW_OCCURRENCE_REPORTS && (
                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                        {/* Source Filter */}
                        <Autocomplete
                            multiple
                            size="small"
                            sx={{ minWidth: 250, flex: 1 }}
                            options={availableSources.map((s): string => s.sourceName)}
                            disableCloseOnSelect
                            disabled={filtersLoading || availableSources.length === 0}
                            value={selectedSources}
                            onChange={(event, value: string[]): void => {
                                setSelectedSources(value);
                                onFilterChange('sourceNames', value);
                            }}
                            renderOption={(props, option, {selected}): JSX.Element => (
                                <Box component="li" {...props}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        checked={selected}
                                        sx={{ marginRight: 1 }}
                                    />
                                    {sourceOptionLabel(option)}
                                </Box>
                            )}
                            renderInput={(params): JSX.Element => (
                                <TextField
                                    {...params}
                                    label="Sources"
                                    placeholder={selectedSources.length === 0 ? 'Select sources...' : ''}
                                    helperText={availableSources.length === 0 ? 'No sources available' : ''}
                                />
                            )}
                        />

                        {/* Descriptor Terms Filter */}
                        <Autocomplete
                            multiple
                            size="small"
                            sx={{ minWidth: 250, flex: 1 }}
                            options={availableDescriptorTerms}
                            groupBy={(option): string => option.descriptorName}
                            getOptionLabel={(option): string => option.term}
                            disableCloseOnSelect
                            disabled={filtersLoading || availableDescriptorTerms.length === 0}
                            value={selectedDescriptorTerms}
                            onChange={(event, value: DescriptorTerm[]): void => {
                                setSelectedDescriptorTerms(value);
                                const termIds = value.map((term): number => term.id);
                                onFilterChange('descriptorTermIds', termIds);
                            }}
                            renderOption={(props, option, {selected}): JSX.Element => (
                                <Box component="li" {...props}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                                        checkedIcon={<CheckBox fontSize="small" />}
                                        checked={selected}
                                        sx={{ marginRight: 1 }}
                                    />
                                    {option.term}
                                </Box>
                            )}
                            renderInput={(params): JSX.Element => (
                                <TextField
                                    {...params}
                                    label="Descriptor Terms"
                                    placeholder={selectedDescriptorTerms.length === 0 ? 'Select descriptor terms...' : ''}
                                    helperText={availableDescriptorTerms.length === 0 ? 'No descriptor terms available' : ''}
                                />
                            )}
                        />
                    </Stack>
                )}

                {/* Filter Row: Source Filters (Foundry Events only) */}
                {currentDataset === ResourceType.FOUNDRY_EVENTS && (
                    <Autocomplete
                        multiple
                        size="small"
                        sx={{ minWidth: 250, flex: 1 }}
                        options={availableSources.map((s): string => s.sourceName)}
                        disableCloseOnSelect
                        disabled={filtersLoading || availableSources.length === 0}
                        value={selectedSources}
                        onChange={(event, value: string[]): void => {
                            setSelectedSources(value);
                            onFilterChange('sourceNames', value);
                        }}
                        renderOption={(props, option, {selected}): JSX.Element => (
                            <Box component="li" {...props}>
                                <Checkbox
                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                    checkedIcon={<CheckBox fontSize="small" />}
                                    checked={selected}
                                    sx={{ marginRight: 1 }}
                                />
                                {option}
                            </Box>
                        )}
                        renderInput={(params): JSX.Element => (
                            <TextField
                                {...params}
                                label="Sources"
                                placeholder={selectedSources.length === 0 ? 'Select sources...' : ''}
                                helperText={availableSources.length === 0 ? 'No sources available' : ''}
                            />
                        )}
                    />
                )}

                {/* Filter Row: Working Area Filter (Component Catalogue only) */}
                {currentDataset === ResourceType.COMPONENT_CATALOGUE && (
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 250, flex: 1 }}
                        options={availableWorkingAreas}
                        getOptionLabel={(grp): string => getResourceNameWithAscendants(grp, ResourceType.GROUPS)}
                        isOptionEqualToValue={(option, value): boolean => option.id === value.id}
                        disabled={availableWorkingAreas.length === 0}
                        value={selectedWorkingArea}
                        onChange={(event, value: Group | null): void => {
                            setSelectedWorkingArea(value);
                            onFilterChange('groupId', value?.id ?? null);
                        }}
                        renderInput={(params): JSX.Element => (
                            <TextField
                                {...params}
                                label="Working Area"
                                placeholder="All working areas"
                                helperText={availableWorkingAreas.length === 0 ? 'No working areas available' : ''}
                            />
                        )}
                    />
                )}

                {/* Second Row: Search Field */}
                {currentDataset === ResourceType.FOUNDRY_EVENTS ? (
                    <TextField
                        size="small"
                        placeholder="Search events..."
                        value={foundrySearchInput}
                        onChange={(e): void => setFoundrySearchInput(e.target.value)}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter') {
                                onSearchChange(foundrySearchInput);
                            }
                        }}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {foundrySearchInput && (
                                        <IconButton
                                            size="small"
                                            onClick={(): void => {
                                                setFoundrySearchInput('');
                                                onSearchChange('');
                                            }}
                                        >
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={(): void => onSearchChange(foundrySearchInput)}
                                        edge="end"
                                    >
                                        <Search fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                ) : (
                    <TextField
                        size="small"
                        placeholder="Search all levels..."
                        value={searchText}
                        onChange={(e): void => onSearchChange(e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={(): void => onSearchChange('')}
                                        edge="end"
                                    >
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>
        ),
        renderBottomToolbar: (currentDataset === ResourceType.SAFETY_MODELS || currentDataset === ResourceType.INTEGRATED_RISK_PICTURES)
            ? (): JSX.Element => {
                const totalItems = (page as any).totalElements || 0;
                const groupCount = data.length;
                const label = currentDataset === ResourceType.SAFETY_MODELS
                    ? `Showing ${groupCount} groups containing ${totalItems} safety models`
                    : `Showing ${totalItems} integrated risk pictures across ${groupCount} groups`;
                return (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
                        {label}
                    </Box>
                );
            }
            : undefined, // Use default pagination for other datasets
    });

    return (
        <>
            <MaterialReactTable table={table} />

            {/* Foundry Events import-source dropdown */}
            {/* Create menu, for datasets that can create more than one kind of thing */}
            <Menu
                anchorEl={createMenuAnchor}
                open={Boolean(createMenuAnchor)}
                onClose={(): void => setCreateMenuAnchor(null)}
            >
                {(toolbar?.createOptions ?? []).map(option => (
                    <MenuItem
                        key={option.label}
                        onClick={(): void => {
                            setCreateMenuAnchor(null);
                            option.onClick();
                        }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={importMenuAnchor}
                open={Boolean(importMenuAnchor)}
                onClose={(): void => setImportMenuAnchor(null)}
            >
                {availableSources.length === 0 ? (
                    <MenuItem disabled>No sources available</MenuItem>
                ) : (
                    availableSources.map((source): JSX.Element => (
                        <MenuItem
                            key={source.sourceName}
                            onClick={(): void => {
                                handleImportSource(source.sourceName);
                            }}
                        >
                            {source.sourceName}
                        </MenuItem>
                    ))
                )}
            </Menu>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={(): void => {
                    setMenuAnchor(null);
                    setSelectedRow(null);
                }}
            >
                {selectedRow && getResourceActions(
                    selectedRow.type,
                    filters?.archived === 'ARCHIVED'
                )
                    .map((action, index) => {
                        const isDisabled = action.isDisabled ? action.isDisabled(selectedRow) : false;
                        const label = action.getLabel ? action.getLabel(selectedRow) : action.label;

                        const menuItem = (
                            <MenuItem
                                onClick={(): void => handleMenuAction(action.type)}
                                disabled={isDisabled}
                            >
                                {label}
                            </MenuItem>
                        );

                        return (
                            <React.Fragment key={action.type}>
                                {action.divider && index > 0 && <Divider />}
                                {action.tooltip ? (
                                    <Tooltip title={action.tooltip} placement="left" arrow>
                                        <span>{menuItem}</span>
                                    </Tooltip>
                                ) : (
                                    menuItem
                                )}
                            </React.Fragment>
                        );
                    })}
            </Menu>
        </>
    );
};

export default ExplorerMRT;